import { useState } from "react";
import { DAYS, TIMES, STATUS, STATUS_ORDER } from "@/lib/constants";
import { cn } from "@/lib/utils";

/**
 * Admin editable availability view.
 * Merges parent's disponibilita with admin overrides (disponibilita_admin).
 * Slots modified by admin get a small white dot indicator.
 *
 * Props:
 *  - original: dict (parent's disponibilita)
 *  - override: dict (admin overrides — controlled)
 *  - onChange(nextOverride)
 */
export default function EditableAvailability({ original, override, onChange }) {
  const [activeStatus, setActiveStatus] = useState("green");

  const orig = original || {};
  const ovr = override || {};

  const effective = (key) => (key in ovr ? ovr[key] : orig[key]);
  const isModified = (key) => key in ovr && ovr[key] !== orig[key];

  const setSlot = (day, time) => {
    const key = `${day}_${time}`;
    const currentEff = effective(key);
    const next = { ...ovr };
    if (currentEff === activeStatus) {
      // Toggle off: if original had same, need explicit "cleared" state. We'll set to opposite behavior:
      // if slot equals active AND is currently an override → remove override (revert to original)
      // if slot equals active AND is original → set an override that "clears" it (undefined). We use null-like marker.
      if (key in ovr) {
        delete next[key];
      } else {
        // Original matched active; set explicit empty? Not supported by schema.
        // Alternative: cycle to no color by setting a different neutral. Since our SlotStatus only has 3 colors,
        // we treat second click on same as "no change" (skip).
        return;
      }
    } else {
      next[key] = activeStatus;
    }
    onChange(next);
  };

  const revertSlot = (day, time) => {
    const key = `${day}_${time}`;
    if (!(key in ovr)) return;
    const next = { ...ovr };
    delete next[key];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {/* Color picker */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mr-1">
          Colore:
        </span>
        {STATUS_ORDER.map((k) => {
          const s = STATUS[k];
          const active = activeStatus === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setActiveStatus(k)}
              data-testid={`edit-color-${k}`}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 border text-xs font-semibold transition-all",
                active
                  ? `${s.bg} text-white border-transparent shadow-sm scale-[1.02]`
                  : `bg-white ${s.text} ${s.border} hover:shadow-sm`
              )}
            >
              <span className={cn("w-2.5 h-2.5 rounded-full", active ? "bg-white/90" : s.bg)} />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-3">
        <div className="min-w-[560px]">
          <div className="grid grid-cols-[64px_repeat(5,minmax(0,1fr))] gap-1.5 mb-1.5">
            <div />
            {DAYS.map((d) => (
              <div key={d.key} className="text-[11px] font-bold uppercase tracking-wider text-slate-600 text-center">
                {d.short}
              </div>
            ))}
          </div>
          {TIMES.map((t) => (
            <div key={t} className="grid grid-cols-[64px_repeat(5,minmax(0,1fr))] gap-1.5 mb-1 items-center">
              <div className="text-[11px] font-mono text-slate-500 text-right pr-2">{t}</div>
              {DAYS.map((d) => {
                const key = `${d.key}_${t}`;
                const s = effective(key);
                const mod = isModified(key);
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setSlot(d.key, t)}
                    onContextMenu={(e) => { e.preventDefault(); revertSlot(d.key, t); }}
                    data-testid={`edit-slot-${d.key}-${t}`}
                    title={mod ? "Modificato — click destro per ripristinare" : (s ? STATUS[s].label : "vuoto")}
                    className={cn(
                      "slot-btn relative h-8 rounded transition-all",
                      s ? STATUS[s].bg : "bg-slate-100",
                      mod && "ring-2 ring-offset-1 ring-sky-400"
                    )}
                  >
                    {mod && (
                      <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-white shadow" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] text-slate-600">
        {STATUS_ORDER.map((k) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={cn("w-2.5 h-2.5 rounded", STATUS[k].bg)} />
            {STATUS[k].label}
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded bg-slate-200 relative">
            <span className="absolute inset-0 rounded ring-2 ring-sky-400 ring-offset-1" />
          </span>
          <span>Modificato da te (click destro per ripristinare)</span>
        </div>
      </div>
    </div>
  );
}
